/**
 * Notifications inbox — historical list of notifications for the user.
 * Tapping a row marks it read and deep-links from the notification payload.
 *
 * Preference toggles live on `/profile/notifications-prefs` — accessed via
 * the gear icon in this screen's header.
 */
import { useCallback } from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { FlashList } from '@shopify/flash-list'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'expo-router'
import Animated from 'react-native-reanimated'
import {
  Bell,
  BellOff,
  Settings as SettingsIcon,
  CheckCheck,
  CalendarCheck,
  CreditCard,
  MessageCircle,
  XCircle,
  Star,
  FileCheck,
  FileX,
  DollarSign,
  Gauge,
  AlertTriangle,
  ArrowRight,
} from '@/components/ui/icons'

import { Pressable } from '@/components/ui/Pressable'
import { TrackedNativePressable as NativePressable } from '@/components/ui/TrackedNativePressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { PageShell } from '@/components/layout/PageShell'
import { useSemanticColors, RADIUS, SPACING, INTERACTION, withAlpha } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import {
  useNotificationsInbox,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type InboxNotification,
} from '@/lib/hooks/use-notifications-inbox'
import { useNotificationsUnreadCount } from '@/lib/hooks/use-notifications-unread'
import { extractSafeScreen } from '@/lib/utils/notification-routes'
import { formatRelative } from '@/lib/utils/dates'

/** Height of one NotificationRow. The loading skeleton stands in for exactly this. */
const NOTIFICATION_ROW_HEIGHT = 72

// Map `data.type` → icon so each notification category has a visual hook
// instead of a sea of identical bells.
function iconForType(type: string | undefined) {
  switch (type) {
    case 'booking_confirmed':
      return CalendarCheck
    case 'payment_captured':
    case 'payout_completed':
      return DollarSign
    case 'payment_failed':
      return CreditCard
    case 'new_message':
      return MessageCircle
    case 'service_request_status':
      return Gauge
    case 'rental_cancelled':
    case 'host_booking_cancelled':
    case 'booking_cancelled_by_host':
    case 'booking_cancelled_by_tago':
    case 'host_booking_cancelled_by_tago':
      return XCircle
    case 'dispute_opened':
    case 'dispute_escalated':
    case 'dispute_resolved':
    case 'trip_disputed':
      return AlertTriangle
    case 'review_posted':
      return Star
    case 'document_approved':
    case 'license_approved':
    case 'vehicle_document_approved':
      return FileCheck
    case 'document_rejected':
    case 'license_rejected':
    case 'vehicle_document_rejected':
      return FileX
    case 'inspection_pickup':
    case 'inspection_dropoff':
      return CalendarCheck
    default:
      return Bell
  }
}

function NotificationRow({
  notification,
  onPress,
  colors,
  language,
}: {
  notification: InboxNotification
  onPress: () => void
  colors: ReturnType<typeof useSemanticColors>
  language: string
}) {
  const type = (notification.data as Record<string, unknown> | null)?.type as string | undefined
  const Icon = iconForType(type)
  const isUnread = !notification.is_read

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={notification.title}
      testID={`notifications.row-${notification.id}`}
      style={{
        flexDirection: 'row',
        gap: SPACING.md,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.md,
        backgroundColor: isUnread ? `${COLORS.coral.DEFAULT}08` : 'transparent',
      }}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 20,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isUnread ? `${COLORS.coral.DEFAULT}18` : colors.surfaceSubtle,
        }}
      >
        <Icon
          size={18}
          color={isUnread ? COLORS.coral.DEFAULT : colors.textSecondary}
          strokeWidth={1.8}
        />
      </View>

      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
          <Text
            numberOfLines={2}
            adjustsFontSizeToFit
            minimumFontScale={0.9}
            style={{
              flex: 1,
              fontSize: 15,
              lineHeight: 20,
              fontFamily: isUnread ? FONTS.bodyBold : FONTS.body,
              color: colors.textPrimary,
            }}
          >
            {notification.title}
          </Text>
          {isUnread && (
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                flexShrink: 0,
                backgroundColor: COLORS.coral.DEFAULT,
              }}
            />
          )}
        </View>
        <Text
          numberOfLines={3}
          style={{
            marginTop: 2,
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            lineHeight: 18,
          }}
        >
          {notification.body}
        </Text>
        <Text
          style={{
            marginTop: SPACING.xs,
            fontSize: 12,
            fontFamily: FONTS.body,
            color: colors.textMuted,
          }}
        >
          {formatRelative(notification.created_at, language)}
        </Text>
      </View>

      <ArrowRight
        size={14}
        color={colors.textMuted}
        strokeWidth={2}
        style={{ alignSelf: 'center', flexShrink: 0 }}
      />
    </Pressable>
  )
}

export default function NotificationsInboxScreen() {
  const { t, i18n } = useTranslation()
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const { isDesktop } = useBreakpoint()
  const inbox = useNotificationsInbox()
  const enter = useEntrance(!inbox.isLoading)
  const unreadCount = useNotificationsUnreadCount()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  const notifications = inbox.data?.pages.flat() ?? []

  const handlePress = useCallback(
    (n: InboxNotification) => {
      if (!n.is_read) markRead.mutate(n.id)
      const screen = extractSafeScreen(n.data)
      if (screen) router.push(screen as never)
    },
    [markRead, router],
  )

  const handleOpenPrefs = useCallback(() => {
    router.push('/(tabs)/profile/notifications-prefs' as never)
  }, [router])

  const desktopMarkAllButton =
    unreadCount > 0 ? (
      <Pressable
        onPress={() => markAllRead.mutate()}
        accessibilityRole="button"
        accessibilityLabel={t('notifications.mark_all_read')}
        testID="notifications.mark-all-read"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs,
          paddingVertical: SPACING.xs,
          paddingHorizontal: SPACING.sm,
          borderRadius: RADIUS.sm,
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        <CheckCheck size={14} color={colors.textSecondary} strokeWidth={2} />
        <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
          {t('notifications.mark_all_short')}
        </Text>
      </Pressable>
    ) : null

  const mobileMarkAllButton =
    unreadCount > 0 ? (
      <Pressable
        onPress={() => markAllRead.mutate()}
        accessibilityRole="button"
        accessibilityLabel={t('notifications.mark_all_read')}
        testID="notifications.mark-all-read"
        style={{
          marginHorizontal: SPACING.lg,
          marginBottom: SPACING.sm,
          minHeight: 44,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.xs,
          paddingVertical: SPACING.sm,
          paddingHorizontal: SPACING.md,
          borderRadius: RADIUS.full,
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        <CheckCheck size={16} color={colors.textSecondary} strokeWidth={2} />
        <Text
          numberOfLines={2}
          adjustsFontSizeToFit
          minimumFontScale={0.9}
          style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
        >
          {t('notifications.mark_all_read')}
        </Text>
      </Pressable>
    ) : null

  const prefsButton = (
    <NativePressable
      onPress={handleOpenPrefs}
      accessibilityRole="button"
      accessibilityLabel={t('notification_prefs.title')}
      testID="notifications.prefs-button"
      hitSlop={12}
      collapsable={false}
      // Same disc as the back control on the other side of the header
      // (components/ui/BackButton). The gear used to be a bare stroke glyph
      // facing a filled, shadowed chip, so the two header actions read as two
      // different levels of control.
      style={{
        width: INTERACTION.minTouchTarget,
        height: INTERACTION.minTouchTarget,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: INTERACTION.minTouchTarget / 2,
        backgroundColor: withAlpha(colors.surface, 0.92),
        borderWidth: 1,
        borderColor: withAlpha(colors.textPrimary, isDark ? 0.14 : 0.08),
        ...WEB_SHADOWS.md,
      }}
    >
      <SettingsIcon size={20} color={colors.textPrimary} strokeWidth={2} />
    </NativePressable>
  )

  return (
    <ScreenContainer variant="reading" scroll={false}>
      <PageShell
        title={t('notifications.title')}
        back
        actions={
          <>
            {desktopMarkAllButton}
            {prefsButton}
          </>
        }
      >
        {!isDesktop && (
          <ScreenHeader
            title={t('notifications.title')}
            right={prefsButton}
            titleNumberOfLines={2}
          />
        )}
        {!isDesktop ? mobileMarkAllButton : null}

        {/* List */}
        {inbox.isLoading ? (
          // Row-shaped placeholders, not a bare spinner: the skeleton has the
          // shape of what it replaces (STANDARDS §8), so the swap to real rows
          // moves nothing.
          <View
            style={{ paddingHorizontal: SPACING.md, gap: SPACING.sm }}
            testID="notifications.skeleton"
          >
            {[0, 1, 2, 3].map((index) => (
              <Skeleton
                key={index}
                width="100%"
                height={NOTIFICATION_ROW_HEIGHT}
                borderRadius={RADIUS.lg}
                staggerIndex={index}
              />
            ))}
          </View>
        ) : inbox.isError ? (
          // A failed fetch is not an empty inbox. This used to render
          // "No tenés notificaciones" to someone whose inbox simply had not
          // loaded — and offered nothing to tap.
          <View style={{ flex: 1, justifyContent: 'center' }} testID="notifications.error-state">
            <EmptyState
              variant="aurora"
              title={t('common.error_title')}
              description={t('common.error_description')}
              action={{ label: t('common.retry'), onPress: () => void inbox.refetch() }}
            />
          </View>
        ) : notifications.length === 0 ? (
          <View style={{ flex: 1, justifyContent: 'center' }}>
            <EmptyState
              variant="aurora"
              illustration={<BellOff size={40} color={colors.textMuted} strokeWidth={1.4} />}
              title={t('notifications.empty_title')}
              description={t('notifications.empty_desc')}
            />
          </View>
        ) : (
          // FlatList → FlashList per CLAUDE.md
          // ("no FlatList — use FlashList or ScrollView for small lists").
          // FlashList v2 auto-measures item size (estimatedItemSize was
          // deprecated). Wrapped in a styled View because FlashList's
          // own `style` prop is more restrictive than FlatList's
          // (border-radius/shadow on the recycler container clips
          // its viewport). Same paginate + refresh + footer behavior
          // preserved.
          <View
            style={{
              flex: 1,
              backgroundColor: colors.surface,
              marginHorizontal: isDesktop ? 0 : SPACING.md,
              borderRadius: RADIUS.lg,
              overflow: 'hidden',
              ...WEB_SHADOWS.sm,
            }}
          >
            <FlashList
              data={notifications}
              keyExtractor={(n) => n.id}
              renderItem={({ item, index }) => (
                <Animated.View collapsable={false} entering={enter(index)}>
                  <NotificationRow
                    notification={item}
                    onPress={() => handlePress(item)}
                    colors={colors}
                    language={i18n.language}
                  />
                  <View
                    style={{
                      height: 1,
                      backgroundColor: colors.border,
                      marginHorizontal: SPACING.md,
                    }}
                  />
                </Animated.View>
              )}
              contentContainerStyle={{
                paddingBottom: insets.bottom + SPACING.lg,
              }}
              refreshControl={
                <BrandedRefreshControl refreshing={inbox.isRefetching} onRefresh={inbox.refetch} />
              }
              onEndReached={() => {
                if (inbox.hasNextPage && !inbox.isFetchingNextPage) inbox.fetchNextPage()
              }}
              onEndReachedThreshold={0.5}
              ListFooterComponent={
                inbox.isFetchingNextPage ? (
                  <View style={{ paddingVertical: SPACING.md }}>
                    <ActivityIndicator color={COLORS.coral.DEFAULT} />
                  </View>
                ) : null
              }
            />
          </View>
        )}
      </PageShell>
    </ScreenContainer>
  )
}
